import { middleware, slices } from '@frontend/redux';
import { authReducer, clearAuth, clearPromo, clearTaskReview, clearTracking } from '@frontend/redux/slices';
import { Env } from '@frontend/web/utils/env';
import type { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

export const logoutMiddleware: Middleware =
  ({ dispatch }: MiddlewareAPI) =>
  (next) =>
  (action) => {
    if (clearAuth.match(action)) {
      dispatch(baseApi.util.resetApiState());
      dispatch(clearTaskReview());
      dispatch(clearPromo());
      dispatch(clearTracking());
      authReducer(undefined, action);
    }

    return next(action);
  };

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'dismissedBandwidthBanners', 'dismissedSuggestions', 'promo', 'task', 'tracking'],
};

export const baseApi = slices.getBaseApiSlice({ apiUrl: Env.VITE_API_BASE_URL, platform: 'web' });

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  auth: slices.authReducer,
  common: slices.commonReducer,
  dismissedBandwidthBanners: slices.dismissedBandwidthBannersReducer,
  dismissedSuggestions: slices.dismissedSuggestionsReducer,
  promo: slices.promoReducer,
  task: slices.taskReducer,
  tracking: slices.trackingReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(middleware.loggingMiddleware(Env.VITE_ENV))
      .concat(baseApi.middleware)
      .concat(middleware.errorMiddleware)
      .concat(logoutMiddleware),
  reducer: persistedReducer,
});

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
